import FingerprintJS from '@fingerprintjs/fingerprintjs'
import getCurrencies from 'api/currencies'
import registerByTelegram from 'api/registerByTelegram'
import { Formik } from 'formik'
import handleResponseError from 'forms/handleResponseError'
import { ICurrency } from 'interfaces/ICurrency'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { currenciesWithTranslations } from 'static/currencies'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import useTelegramMiniapp from 'utils/hooks/useTelegramMiniapp'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'
import * as yup from 'yup'
import validateMessage from './validateMessage'

import { useTranslation } from 'next-i18next'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Form from 'components/Form/Form'
import Select from 'components/Form/Select/Select'
import { WiFiIcon } from 'components/Images/Icons'
import Link from 'components/Link'
import SubmitButton from 'components/SubmitButton'

import styles from './RegisterForm.module.scss'

const ChooseCurrencyForm = () => {
  const [t, i18n] = useTranslation(['main', 'error', 'shared'])

  const savedLanguage = useLanguageForSave(i18n)

  const [error, setError] = useState<{ icon?: FC; message: string }>()
  const [currencies, setCurrencies] = useState([])

  useEffect(() => {
    const fetchCurrencies = async () => {
      const res = await getCurrencies()
      if (res.data.status !== 'success') {
        toast(handleResponseError(res))
      } else {
        const items: ICurrency[] = Object.values(res?.data?.payload?.items) || []
        setCurrencies(items)
      }
    }

    fetchCurrencies()
  }, [setCurrencies])

  const currencyFieldValues = useMemo(() => {
    return currencies
      .filter((item) => item.isCanRegistered)
      .map((item) => {
        const translationKey = currenciesWithTranslations.find(
          (currency) => currency.code === item.code,
        )?.translationKey
        return {
          name: translationKey ? t(translationKey) : item.name,
          value: item.id,
          icon: item.icon,
          label: item.code,
          countries: item.countries,
        }
      })
  }, [currencies, t])

  const [isLoading, toggleIsLoading] = useState(false)
  const [isSucceed, toggleIsSucceed] = useState(false)

  const { getWebAppAsync } = useTelegramMiniapp()

  const onSubmit = useCallback(
    async (values) => {
      setError(undefined)
      toggleIsLoading(true)

      try {
        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()
        const webApp = await getWebAppAsync()

        const [partnerId, subid3, companyId, countryCode] = webApp?.initDataUnsafe.start_param
          ? (webApp?.initDataUnsafe.start_param as string).split('_')
          : []

        const res = await registerByTelegram({
          visitorId: fpResponse.visitorId,
          language: savedLanguage,
          preferredCurrency: values.currency.value,
          initData: webApp?.initData,
          partnerId,
          companyId,
          countryCode,
          subid3,
        })

        if (res.data.status !== 'success') {
          setError({ icon: WiFiIcon, message: validateMessage.serverError })
        } else {
          toggleIsSucceed(true)
          setError(undefined)

          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)

        setError({ icon: WiFiIcon, message: validateMessage.serverError })
      } finally {
        toggleIsLoading(false)
      }
    },
    [setError, getWebAppAsync],
  )

  return (
    <Formik
      initialValues={{
        currency: undefined,
      }}
      validationSchema={yup.object({
        currency: yup.object().required(t('error:requiredField')),
      })}
      onSubmit={onSubmit}
    >
      <Form autoComplete="off">
        <Select
          options={currencyFieldValues}
          name="currency"
          placeholder={t('main:signUpCurrency')}
        />
        {error && <ErrorMessage icon={error.icon} message={error.message} />}
        <div className={styles.submit}>
          <SubmitButton id="authSignUpButton" isLoading={isLoading} isSucceed={isSucceed}>
            {t('shared:confirm')}
          </SubmitButton>
        </div>
        <div className={styles.additional}>
          {t('main:signUpConfirmSignUpFirst')}
          <Link href="/regulation/terms-and-conditions" className={styles.link} target="_blank">
            {t('main:signUpConfirmSignUpSecond')}
          </Link>
          {t('main:signUpConfirmSignUpThird')}
          <Link href="/regulation/privacy-policy" className={styles.link} target="_blank">
            {t('main:signUpConfirmSignUpFouth')}
          </Link>
        </div>
      </Form>
    </Formik>
  )
}

export default ChooseCurrencyForm
